import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { concat, EMPTY, Observable, of, pipe, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, takeWhile, tap, toArray } from 'rxjs/operators';
import { cloneDeep, flatten, merge, set } from 'lodash';

import { environment } from '@env/environment';
import { CredentialsService, I18nService, untilDestroyed, UrlParamsService } from '@app/core';
import {
  AppService,
  AuthenticationService,
  DocumentService,
  DynamicDocumentService,
  EnterpriseService,
  ErrorHandlerService,
  GoogleAnalyticsService,
  NotyService,
  OAuthService,
  PadesSignatureData,
  PhoneOtpProviderService,
  UserService,
  VerificationService,
  WhitelabelService
} from '@app/services';
import {
  AuthenticationMethod,
  CustomSigner,
  DeliveryMethodEnum,
  Document,
  DynamicDocumentData,
  DynamicDocumentFieldsTypes,
  DynamicTemplateThemeEnum,
  DynamicTemplateVariable,
  FallbackBehaviorEnum,
  FieldDataToShow,
  FieldsValuesBySigner,
  FloatingField,
  GTMEventType,
  InlineFieldContext,
  PrefilledFields,
  Purpose,
  ReminderEnum,
  SecurityVerificationEnum,
  SecurityVerificationType,
  SignatureToken,
  SignatureTypeEnum,
  SignatureTypesEnum,
  Signer,
  SignerFieldsInput,
  SignerTypeEnum,
  User,
  Whitelabel
} from '@app/models';
import { AlertModalService, ConfirmOtpModalService, LoaderService, PdfViewerComponent, SetMissingUserInfoModalService } from '@app/shared';
import { DocumentsListingService } from '../shared/documents-listing.service';
import { AuthLoginModalService } from './auth-login-modal/auth-login-modal.service';
import { CreateSignerModalService } from '../shared/create-signer-modal/create-signer-modal.service';
import { UpdateSignerModalService } from './update-signer-modal/update-signer-modal.service';
import { SignLinkModalService } from './sign-link-modal/sign-link-modal.service';
import { ConsultCPFModalService } from './consult-cpf-modal/consult-cpf-modal.service';
import { DocumentHistoryComponent } from './document-history-sidebar/document-history-sidebar.component';
import { DocumentCreatedModalService } from './document-created-modal/document-created-modal.service';
import { DocumentSignedModalService } from './document-signed-modal/document-signed-modal.service';
import { PreLoginModalService } from './pre-login-modal/pre-login-modal.service';
import { UserExistsModalService } from './user-exists-modal/user-exists-modal.service';
import { ChooseSignatureModalService } from './choose-signature-modal/choose-signature-modal.service';
import { PreSignModalService } from '../shared/pre-sign-modal/pre-sign-modal.service';
import { RejectModalService } from './reject-modal/reject-modal.service';
import { VerificationSmsModalService } from '@app/pages/documents/shared/verification-sms-modal/verification-sms-modal.service';
import { VerificationIdAnalyzerModalService } from '@app/pages/documents/shared/verification-id-analyzer-modal/verification-id-analyzer-modal.service';
import { VerificationModalService } from '@app/pages/documents/shared/verification-modal/verification-modal.service';
import { ManualBiometryApprovalEnum, VerificationDataModalService } from '@app/pages/documents/shared/verification-data-modal/verification-data-modal.service';
import { SmsLoginModalService } from '@app/shared/sms-login-modal/sms-login-modal.service';
import { AuthEmailConfirmationModalService } from '@app/pages/documents/documents-show/auth-email-confirmation-modal/auth-email-confirmation-modal.service';
import { DynamicDocumentViewerComponent } from '../shared/dynamic-document-viewer/dynamic-document-viewer.component';
import { AuthBiometricLoginModalService } from '@app/pages/documents/documents-show/auth-biometric-login-modal/auth-biometric-login-modal.service';

@Component({
  selector: 'app-documents-show',
  templateUrl: './documents-show.component.html',
  styleUrls: ['./documents-show.component.scss'],
  standalone: false
})
export class DocumentsShowComponent implements OnInit, OnDestroy {
  isLoading = false;
  currentUser: User;
  document: Document;
  currentSigners: CustomSigner[] = [];
  floatingFields: FloatingField[];
  isShowingSignersMobile = false;
  isShowingHistoryMobile = false;
  isHistoryAlreadyOpened = false;
  isShowingMobileMenu = false;
  doABarrelScroll = true;
  isShowingMobileMenuDownloads = false;
  isShowingMobileMenuAlteration = false;
  maxScroll: number;
  screen = document.documentElement;
  sidebarContainer: 'signers' | 'history' = 'signers';
  backLink = '/documentos/todos';
  isDocumentWhitelabelLoaded = false;
  isMobile!: boolean;
  mobileReview = false;
  showFieldsForm = false;

  templateVariables: DynamicTemplateVariable[];
  fieldsToFill: DynamicTemplateVariable[] = [];
  originalFieldsOrder: DynamicTemplateVariable[] = [];
  filledFieldsValues: Record<string, string>;
  alreadyFilledFields: FieldsValuesBySigner[];
  signersFieldsData?: Record<string, FieldDataToShow[]>;
  signerDynamicIds: number[];

  documentHTML?: string;
  dynamicDocumentTheme = DynamicTemplateThemeEnum.Modern;
  renderedDocumentScale = 1;

  readonly ReminderEnum = ReminderEnum;
  readonly DynamicDocumentFieldsTypes = DynamicDocumentFieldsTypes;
  readonly InlineFieldContext = InlineFieldContext;

  @ViewChild('pdfViewer', { static: false }) pdfViewer: PdfViewerComponent;
  @ViewChild('historyComponent', { static: false }) historyComponent: DocumentHistoryComponent;
  @ViewChild('historyComponentMobile', { static: false }) historyComponentMobile: DocumentHistoryComponent;
  @ViewChild('renderedTemplate', { static: false }) renderedTemplate!: DynamicDocumentViewerComponent;

  documentWhitelabel: Whitelabel = {
    main_color: null,
    logo: '',
    pdf_logo: '',
    pdf_logo_mini: ''
  };
  private navigationState: { [k: string]: any };

  constructor(
    public appService: AppService,
    public documentService: DocumentService,
    public userService: UserService,
    public urlParamsService: UrlParamsService,
    public enterpriseService: EnterpriseService,
    public route: ActivatedRoute,
    private router: Router,
    private notyService: NotyService,
    private alertModalService: AlertModalService,
    private errorHandlerService: ErrorHandlerService,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private credentialsService: CredentialsService,
    private authenticationService: AuthenticationService,
    private oauthService: OAuthService,
    private loaderService: LoaderService,
    private whitelabelService: WhitelabelService,
    private authLoginModalService: AuthLoginModalService,
    private createSignerModalService: CreateSignerModalService,
    private updateSignerModalService: UpdateSignerModalService,
    private signLinkModalService: SignLinkModalService,
    private consultCPFModalService: ConsultCPFModalService,
    private preLoginModalService: PreLoginModalService,
    private documentCreatedModalService: DocumentCreatedModalService,
    private documentSignedModalService: DocumentSignedModalService,
    private userExistsModalService: UserExistsModalService,
    private chooseSignatureModalService: ChooseSignatureModalService,
    private setMissingUserInfoModalService: SetMissingUserInfoModalService,
    private preSignModalService: PreSignModalService,
    private rejectModalService: RejectModalService,
    private verificationSmsModalService: VerificationSmsModalService,
    private verificationIdAnalyzerModalService: VerificationIdAnalyzerModalService,
    private verificationModalService: VerificationModalService,
    private verificationDataModalService: VerificationDataModalService,
    private smsLoginModalService: SmsLoginModalService,
    private confirmOtpModalService: ConfirmOtpModalService,
    private authEmailConfirmationModalService: AuthEmailConfirmationModalService,
    private changeDetectorRef: ChangeDetectorRef,
    private dynamicDocumentService: DynamicDocumentService,
    private listingService: DocumentsListingService,
    private authBiometricLoginModalService: AuthBiometricLoginModalService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private phoneOtpProvider: PhoneOtpProviderService,
    private verificationService: VerificationService
  ) {
    this.navigationState = router.getCurrentNavigation().extras.state || {};
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
      next: () => {
        this.updateBackLink();
      }
    });
  }

  ngOnInit() {
    this.userService
      .watchCurrentUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: user => (this.currentUser = user)
      });

    (this.credentialsService.isAuthenticated() ? this.userService.getCurrentUser({ fetchPolicy: 'cache-first' }) : of(null))
      .pipe(
        finalize(() => this.initLoadDocument()),
        catchError(() => of(null))
      )
      .subscribe();

    if (!this.appService.isAutentiqueDomain()) {
      this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' }).subscribe({
        next: whitelabel => {
          this.isDocumentWhitelabelLoaded = true;
          this.documentWhitelabel = this.whitelabelService.buildWhitelabel(whitelabel);
        }
      });
    }

    if (!this.isSignaturePage()) {
      this.appService.loadStatusPageWidget();
    }
    this.updateBackLink();
  }

  initLoadDocument() {
    this.loadDocument(this.route.snapshot.params.id)
      .pipe(
        switchMap(([document, currentSigners]) => {
          if (!document.is_dynamic_document) {
            return of({ document, currentSigners });
          }
          return this.setDynamicDocumentData(document.files.original).pipe(map(() => ({ document, currentSigners })));
        })
      )
      .subscribe({
        next: ({ document, currentSigners }) => {
          if (document.locale.language && !this.currentUser) {
            this.i18nService.language = document.locale.language;
          }

          if (this.appService.isAutentiqueDomain()) {
            this.whitelabelService.loadWhitelabelByDocument({ documentId: document.id }, { fetchPolicy: 'cache-first' }).subscribe({
              next: whitelabel => {
                this.isDocumentWhitelabelLoaded = true;
                this.documentWhitelabel = this.whitelabelService.buildWhitelabel(whitelabel);
                if (whitelabel.pdf_logo_mini) {
                  this.whitelabelService.setMetaTags({ iconUrl: whitelabel.pdf_logo_mini });
                }
              }
            });
          }

          if (this.navigationState.fromCreateDocument) {
            history.replaceState({}, ''); // clear navigationState.fromCreateDocument to avoid reloading the document
            this.documentCreatedModalService
              .open({ document, currentSigner: currentSigners[0], signerHasFieldsToFill: this.fieldsToFill?.length > 0 })
              .pipe(
                switchMap(() =>
                  this.setMissingUserInfoModalService.openIfMissingUserInfo({
                    ignoreCpf: this.document.ignore_cpf,
                    forceBirthdateAs: currentSigners[0] && currentSigners[0].configs?.birthdate,
                    forceCpfAs: !this.document.qualified && currentSigners[0] && currentSigners[0].configs?.cpf,
                    forceNameAs: currentSigners[0] && currentSigners[0].configs?.name,
                    ignoreBirthdate: this.document.ignore_birthdate,
                    prefilledFields: currentSigners[0].configs?.prefilled_fields,
                    isSigningFlow: true
                  })
                ),
                switchMap(() => this.checkFillFields()),
                switchMap(() => this.openPreSignModal() as Observable<PadesSignatureData>),
                switchMap(data => {
                  // A3 Desktop success skip
                  if (data?.isA3DesktopSuccess) {
                    const signedSigner = (data.signer as Signer) || this.currentSigners[0];
                    this.currentSigners = [];
                    this.getDocument(this.document.id).subscribe();
                    if (this.isHistoryAlreadyOpened) this.historyComponent.refreshHistory();
                    this.documentSignedModalService.open({ currentSigner: signedSigner, document: data.document || this.document }).subscribe();
                    return EMPTY;
                  }
                  // Standard signing flow
                  return this.signDocumentRequest(this.currentSigners, data);
                }),
                tap(() => this.documentSignedModalService.open({ currentSigner: currentSigners[0], document }).subscribe())
              )
              .subscribe({
                error: error => this.errorHandlerService.handle(error)
              });
          } else if (this.route.snapshot.queryParams?.signPrompt && this.canBeSigned(document, currentSigners[0]) && this.documentService.isSignerTurn(document, currentSigners[0])) {
            this.signDocumentPrompt();
          }
        }
      });
    if (!this.appService.isAutentiqueDomain()) {
      this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' }).subscribe({
        next: whitelabel => {
          this.isDocumentWhitelabelLoaded = true;
          this.documentWhitelabel = this.whitelabelService.buildWhitelabel(whitelabel);
        }
      });
    }

    if (!this.isSignaturePage()) {
      this.appService.loadStatusPageWidget();
    }
    this.updateBackLink();

    this.isMobile = window.innerWidth <= 992;
  }

  ngOnDestroy() {}

  loadDocument(id: string) {
    return new Observable<[Document, Signer[]]>(subscriber => {
      id = id.split('?')[0]; // Gambiarra para evitar que o ID venha com parâmetros de query string, o que pode acontecer em casos de assinatura por link
      id = (id || 'empty').replace(/[^a-z0-9]/gi, ''); // Gambiarra pra evitar erros caso o ID não esteja no padrão do tipo "UUID" do GraphQL

      let signature$;
      let signTokens$ = this.documentService.authSignToken({ documentsIds: [id] });

      if (this.documentService.isSignatureByLink(id)) {
        signature$ = this.documentService.linkSignature({ documentId: id });
        signTokens$ = null;
      } else if (this.isSignaturePage()) {
        signature$ = this.documentService.signature({ documentId: id });
        signTokens$ = this.documentService.publicSignToken({ signatureId: id });
      } else if (!this.credentialsService.isAuthenticated()) {
        signTokens$ = null;
      }

      if (signTokens$) {
        signTokens$ = signTokens$.pipe(
          this.setCurrentSignersPipe(),
          tap({
            next: () => this.viewDocument(this.currentSigners)
          }),
          tap({
            next: () => subscriber.next([this.document, this.currentSigners]),
            error: error => subscriber.error(error),
            complete: () => subscriber.complete()
          })
        );
      }

      if (signature$) {
        signature$.subscribe({
          next: signature =>
            this.getDocument(signature.document.id)
              .pipe(
                // Signatário temporário (links)
                tap({
                  next: () => (this.currentSigners = [{ signature_id: null, ...this.document.signatures.find(sig => sig.public_id === signature.public_id) }])
                }),
                switchMap(doc => {
                  const currentSigner = doc.signatures.find(sig => sig.public_id === signature.public_id);
                  if (this.documentService.isPastDeadline(doc) && !this.isOwner() && currentSigner && this.documentService.isSignerPending(currentSigner, { ignoreEmailErrors: true })) {
                    this.router.navigate(['/404'], { replaceUrl: true, state: { fromDocumentUrl: location.href } });
                    return EMPTY;
                  } else {
                    return of(doc);
                  }
                }),
                tap({
                  next: () => signTokens$ && signTokens$.subscribe()
                })
              )
              .subscribe({
                next: doc => !signTokens$ && subscriber.next([doc, []]),
                error: error => !signTokens$ && subscriber.error(error),
                complete: () => !signTokens$ && subscriber.complete()
              }),
          error: error => {
            const handledError = this.errorHandlerService.handle(error, { ignoreNotificationOn: ['signature_not_found', 'link_not_found', 'document_not_found'] });
            subscriber.error(error);
            if (!this.appService.isTokenInvalidError(handledError.message)) {
              this.router.navigate(['/404'], { replaceUrl: true, state: { fromDocumentUrl: location.href } });
            }
          }
        });
      } else {
        this.getDocument(id)
          .pipe(
            tap({
              next: () => signTokens$ && signTokens$.subscribe()
            })
          )
          .subscribe({
            next: doc => !signTokens$ && subscriber.next([doc, []]),
            error: error => !signTokens$ && subscriber.error(error),
            complete: () => !signTokens$ && subscriber.complete()
          });
      }
    });
  }

  getDocument(id: string) {
    return this.documentService.documentWithFloatingFields({ id }).pipe(
      tap({
        next: ({ document, floatingFields }) => {
          this.document = document;
          this.floatingFields = floatingFields;

          setTimeout(() => {
            this.maxScroll = this.screen.scrollHeight - this.screen.clientHeight;
            if (this.maxScroll === 0 && this.scrollingRequired()) {
              this.doABarrelScroll = false;
            }
          }, 10000);
          if (!this.documentService.isPastLifecycle(this.document) && !this.document.files?.original) {
            setTimeout(
              () =>
                this.documentService
                  .retryDocumentFile({ id })
                  .pipe(untilDestroyed(this))
                  .subscribe({
                    next: data => (this.document.files.original = data.files.original)
                  }),
              2000
            );
          }
        },
        error: error => {
          const handledError = this.errorHandlerService.handle(error, { ignoreNotificationOn: ['document_not_found'] });
          if (!this.appService.isTokenInvalidError(handledError.message)) {
            this.router.navigate(['/404'], { replaceUrl: true, state: { fromDocumentUrl: location.href } });
          }
        }
      }),
      map(({ document }) => document)
    );
  }

  viewDocument(signers: CustomSigner[]) {
    if (!this.documentService.isSignatureByLink(this.route.snapshot.params.id)) {
      const signatureIds = signers
        .filter(signer => !signer.viewed || !signer.viewed.created_at)
        .map(signer => signer.signature_id)
        .filter(id => !!id);
      this.documentService.view(signatureIds).subscribe({
        next: data => {
          const signatures = cloneDeep(this.document.signatures);
          (data || []).forEach(item =>
            set(
              signatures.find(sig => sig.public_id === item.public_id),
              'viewed',
              item.viewed
            )
          );
          this.document.signatures = signatures;
        },
        error: error => this.errorHandlerService.handle(error)
      });
    }
  }

  setDynamicDocumentData(documentFile: string) {
    return this.dynamicDocumentService.getDynamicDocumentData(documentFile).pipe(
      tap((data: DynamicDocumentData) => {
        this.templateVariables = data.variables;
        this.dynamicDocumentTheme = data.theme || DynamicTemplateThemeEnum.Modern;
        this.documentHTML = this.appService.cleanAttqDocumentMargins(data.content);
      }),
      tap(() => {
        this.alreadyFilledFields = this.appService.getAlreadyFilledFields(this.document, this.templateVariables);
        this.setCurrentSignerFields();
      })
    );
  }

  updateRenderedDocumentScale() {
    this.renderedDocumentScale = this.appService.scaleRenderedDocumentToFit(this.renderedTemplate?.editorRef.nativeElement, this.isMobile, { widthFix: 384, margin: 4 });
    this.changeDetectorRef.detectChanges();
  }

  verifySignerHasFields(signer: Signer) {
    if (signer && this.templateVariables) {
      return Object.values(this.templateVariables)?.some(variable => signer.dynamic_ids?.includes(variable.data_signer_id));
    }
    return false;
  }

  setCurrentSignerFields() {
    this.originalFieldsOrder = [];
    this.fieldsToFill = [];

    if (this.currentSigners[0]?.fields_filled && this.currentSigners[0]?.fields_filled.created_at) {
      this.fieldsToFill = null;
    } else {
      const currentUserId = this.currentSigners[0]?.user?.id;
      this.signerDynamicIds = this.currentSigners
        .filter(signer => signer.user?.id === currentUserId)
        .map(signer => signer.dynamic_ids)
        .flat()
        .concat(this.currentSigners[0]?.dynamic_ids)
        .filter((id, index, self) => id && self.indexOf(id) === index);

      this.originalFieldsOrder = this.originalFieldsOrder.concat(Object.values(this.templateVariables || []).filter(variable => (this.signerDynamicIds || []).includes(variable.data_signer_id)));
      this.fieldsToFill = this.fieldsToFill.concat(this.originalFieldsOrder);
    }

    this.fieldsToFill = this.appService.removeDuplicateFields(this.fieldsToFill);
  }

  startFillDocument() {
    if (this.documentHTML) {
      this.showFieldsForm = true;

      setTimeout(() => {
        // Para formatar os campos checkbox
        // (document.querySelector(
        //   '.document-rendered--with-fields.ql-editor'
        // ) as HTMLDivElement).innerHTML = /* this.domSanitizer.bypassSecurityTrustHtml( */ this.document.template_text /* ) as string */;
        // (document.querySelectorAll(".field--inline[data-field-type='checkboxField']")).forEach((checkbox: HTMLElement) => {
        //   checkbox.innerHTML = '<i class="fal fa-square"></i>';
        // });

        const e = document.querySelectorAll('.field--inline');
        e.forEach(el => el.removeAttribute('draggable'));

        (document.querySelectorAll(`.field__holder input`)[0] as HTMLElement)?.focus();
      });
    }
  }

  mobileProceedToReview() {
    this.showFieldsForm = false;
    this.mobileReview = true;
  }

  submitFilledFields(result: any) {
    this.filledFieldsValues = result;

    if (this.isMobile) {
      this.mobileProceedToReview();
    } else {
      this.isLoading = true;
      this.signDocumentPrompt();
    }
  }

  checkFillFields(): Observable<PadesSignatureData> {
    const needsToFill = this.document.is_dynamic_document && this.fieldsToFill?.length > 0 && !this.filledFieldsValues;

    if (needsToFill) {
      this.startFillDocument();
      return EMPTY;
    }

    return of(null);
  }

  fillFields(isRejection?: boolean) {
    const signerFields: SignerFieldsInput[] = [];

    this.fieldsToFill.forEach((field: DynamicTemplateVariable) => {
      document.querySelectorAll(`.field--view[data-signer-id="${field.data_signer_id}"]`).forEach((userField: HTMLElement) => {
        if (userField.getAttribute('data-field-name') == field.name && (isRejection || this.filledFieldsValues[field.name] == '')) {
          userField.innerText = ' ';
          userField.classList.add('field--filled');
        }
      });
    });

    const dynamicIds = this.signerDynamicIds.filter(id => this.fieldsToFill.find(field => field.data_signer_id == id));
    dynamicIds.forEach(dynamicId => {
      const public_id = this.document.signatures.find(signer => signer.dynamic_ids?.includes(dynamicId)).public_id;
      signerFields.push({
        public_id,
        field_values: this.originalFieldsOrder
          .filter(field => field.data_signer_id === dynamicId)
          .map((field: DynamicTemplateVariable) => {
            return isRejection ? ' ' : this.filledFieldsValues[field.name] || ' ';
          })
      });
    });

    return this.documentService.fillDynamicDocumentFieldsMutation({ signer_fields: signerFields }).pipe(
      tap({
        next: () =>
          setTimeout(() => {
            this.showFieldsForm = false;
            this.mobileReview = false;
            this.fieldsToFill = null;
          }),
        error: error => {
          this.errorHandlerService.handle(error);
          return EMPTY;
        }
      })
    );
  }

  signDocumentPrompt() {
    const currentSigner = this.currentSigners[0];
    const pfFacialMatchVerification: SecurityVerificationType = currentSigner?.verifications?.find(v => v.type === SecurityVerificationEnum.PfFacialMatch);

    if (this.document && this.document.refusable && this.document.stop_on_rejected && this.documentService.isRejected(this.document, { ignoreEmailErrors: true })) {
      this.notyService.warning(this.translateService.instant('notyService.cannotSignAfterRejection'));
      return;
    }

    let loginStep$: Observable<any>;

    if (this.credentialsService.isAuthenticated() && (this.currentUser?.id === currentSigner?.user?.id || !currentSigner?.user)) {
      // já logado, não precisa autenticar
      loginStep$ = of(null);
    } else if ((this.isPhoneDeliveryMethod(currentSigner) && currentSigner?.signature_id) || (!this.currentUser && !this.documentService.isSignatureByLink(this.route.snapshot.params.id))) {
      // Logar passwordless e assinar com link por Email, SMS e WhatsApp se tiver signature_id
      loginStep$ = this.loginPasswordLessFlow(currentSigner);
    } else if (this.isPhoneDeliveryMethod(currentSigner)) {
      // Logar e assinar SMS/WhatsApp
      loginStep$ = this.loginSmsFlow(currentSigner);
    } else if (currentSigner?.user?.email && currentSigner?.user?.cpf && pfFacialMatchVerification && pfFacialMatchVerification.max_attempt > 0 && !pfFacialMatchVerification.verified_at) {
      // logar e assinar por biometria
      loginStep$ = this.loginBiometricFlow(currentSigner);
    } else if (currentSigner?.user?.email && currentSigner.user.hp_set && !this.appService.isAutentiqueDomain()) {
      // Logar com OTP ao inves de senha
      loginStep$ = this.registerOrConfirmEmailFlow(currentSigner);
    } else if (currentSigner?.user?.email && currentSigner.user.hp_set) {
      // Logar e assinar email
      loginStep$ = this.loginEmailFLow(currentSigner);
    } else if (currentSigner?.user?.email && !currentSigner.user.hp_set) {
      // Cadastro e assinar
      loginStep$ = this.registerOrConfirmEmailFlow(currentSigner);
    } else {
      // Logar/Cadastrar e assinar por link
      loginStep$ = this.preLoginFlow(currentSigner);
    }

    this.signingFlow(loginStep$).subscribe();
  }

  signingFlow(loginStep$: Observable<any>) {
    this.isLoading = true;
    return of(null).pipe(
      switchMap(() => this.checkFillFields()),
      switchMap(() => loginStep$),
      switchMap(() => this.openPreSignModal()),
      switchMap(data => {
        // A3 Desktop success skip
        if (data?.isA3DesktopSuccess) {
          const signedSigner = (data.signer as Signer) || this.currentSigners[0];
          this.currentSigners = [];
          this.getDocument(this.document.id).subscribe();
          if (this.isHistoryAlreadyOpened) this.historyComponent.refreshHistory();
          return this.documentSignedModalService.open({ currentSigner: signedSigner, document: data.document || this.document });
        }
        // Standard signing flow
        return this.signDocument(data);
      }),
      finalize(() => (this.isLoading = false)),
      catchError(error => {
        this.errorHandlerService.handle(error);
        return EMPTY;
      })
    );
  }

  loginPasswordLessFlow(currentSigner: CustomSigner) {
    return this.authenticationService.loginSignature({ signature_id: currentSigner.signature_id }).pipe(
      switchMap(credentials => (credentials.mfa.length > 0 ? this.confirmOtpModalService.open({ logoutOnDismiss: true }) : of(credentials))),
      switchMap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' }))
    );
  }

  loginSmsFlow(currentSigner: CustomSigner) {
    return this.smsLoginModalService.open({
      type: this.phoneOtpProvider.authenticationMethod(this.phoneOtpProvider.primaryProviderForPhone(currentSigner.user.phone)),
      phoneNumber: currentSigner.user.phone
    });
  }

  loginEmailFLow(currentSigner: CustomSigner) {
    return this.authLoginModalService.open({ signer: currentSigner, documentLocale: this.document.locale });
  }

  registerOrConfirmEmailFlow(currentSigner: CustomSigner) {
    return this.authEmailConfirmationModalService.open({ email: currentSigner.user.email, sendVerification: true, documentLocale: this.document.locale }).pipe(
      switchMap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' })),
      switchMap(() => this.loadDocument(this.route.snapshot.params.id)),
      catchError(error => {
        this.errorHandlerService.handle(error);
        return EMPTY;
      })
    );
  }

  loginBiometricFlow(currentSigner: CustomSigner) {
    return this.authBiometricLoginModalService.open({ signer: currentSigner }).pipe(
      switchMap(({ option }) => {
        if (option === AuthenticationMethod.Email) {
          if (currentSigner.user.hp_set) {
            return this.loginEmailFLow(currentSigner);
          } else {
            return this.registerOrConfirmEmailFlow(currentSigner);
          }
        } else if (option === AuthenticationMethod.Biometric) {
          return of(null).pipe(
            this.handleVerifications([SecurityVerificationEnum.PfFacialMatch]),
            switchMap(() => (this.credentialsService.credentials?.mfa?.length > 0 ? this.confirmOtpModalService.open({ logoutOnDismiss: true }) : of(null))),
            switchMap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' }))
          );
        }
        return of(null);
      })
    );
  }

  preLoginFlow(currentSigner: CustomSigner) {
    return this.preLoginModalService.open().pipe(
      switchMap(({ option, additionalInfo }) => {
        if (option === AuthenticationMethod.Email) {
          return this.userExistsModalService.open().pipe(
            switchMap(({ userExists, email }) => {
              return userExists
                ? this.authLoginModalService.open({ signer: currentSigner, email, documentLocale: this.document.locale })
                : this.authEmailConfirmationModalService.open({ email, sendVerification: true, documentLocale: this.document.locale });
            }),
            switchMap(() => this.loadDocument(this.route.snapshot.params.id))
          );
        } else if ([AuthenticationMethod.Google, AuthenticationMethod.Facebook, AuthenticationMethod.Microsoft].includes(option) || this.phoneOtpProvider.isPhoneAuthentication(option)) {
          this.isLoading = true;
          return this.oauthService.login(option, { ...additionalInfo, forceUserCreation: true }).pipe(
            switchMap(() => this.loadDocument(this.route.snapshot.params.id)),
            finalize(() => {
              this.isLoading = false;
              this.loaderService.hide();
            })
          );
        }
        return of(null);
      })
    );
  }

  rejectDocumentPrompt() {
    this.rejectModalService
      .open()
      .pipe(
        switchMap(data => this.rejectDocumentRequest(this.currentSigners, data.reason || null)),
        catchError(error => {
          this.errorHandlerService.handle(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  resendSignatures(signer?: Signer) {
    const signers = signer
      ? [signer]
      : this.document.signatures.filter(
          sig =>
            sig.delivery_method !== DeliveryMethodEnum.DeliveryMethodLink &&
            !this.isOwner(sig.user) &&
            this.documentService.isSignerPending(sig, { ignoreEmailErrors: true }) &&
            !this.documentService.isSignerUnapproved(sig)
        );

    if (signers.length > 0) {
      const creditsPrice = signers.reduce((total, sig) => total + this.appService.creditPriceByType(sig.delivery_method), 0);
      this.alertModalService
        .confirmation({ text: this.translateService.instant('alerts.resendDocument'), creditsPrice })
        .pipe(
          tap(() => this.loaderService.show()),
          switchMap(() => this.documentService.resendSignatures({ publicIds: signers.map(sig => sig.public_id) })),
          tap(() => (creditsPrice > 0 ? this.userService.currentUserCredits().subscribe() : null)),
          finalize(() => this.loaderService.hide())
        )
        .subscribe({
          next: () => {
            this.notyService.success(this.translateService.instant('notyService.' + (signers.length > 1 ? 'documentResentToSignatories' : 'documentResentToSignatory')));
            signers.forEach(sig => (sig.email_events.refused = null));
            this.getDocument(this.document.id).subscribe();
            if (this.isHistoryAlreadyOpened) {
              this.historyComponent.refreshHistory();
            }
          },
          error: error => this.errorHandlerService.handle(error)
        });
    }
  }

  deleteDocument() {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.deleteDocument'),
        confirmButtonText: this.translateService.instant('button.exclude')
      })
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          return this.documentService.delete([this.document.id]).pipe(finalize(() => (this.isLoading = false)));
        })
      )
      .subscribe({
        next: () => {
          this.notyService.success(this.translateService.instant('notyService.documentDeletedSuccess'));
          this.router.navigate(['/documentos/todos'], { replaceUrl: true });
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  deleteSigner(signer: Signer) {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.removeSignatory')
      })
      .pipe(
        tap(() => (this.isLoading = true)),
        switchMap(() => this.documentService.deleteSigner({ documentId: this.document.id, signerPublicId: signer.public_id })),
        tap(() =>
          this.loadDocument(this.document.id).subscribe(([doc, signers]) => {
            if (doc.is_dynamic_document) {
              this.alreadyFilledFields = this.appService.getAlreadyFilledFields(doc, this.templateVariables);
            }
          })
        ),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        error: error => this.errorHandlerService.handle(error)
      });
  }

  openCreateSignerModal() {
    this.createSignerModalService.open({ document: this.document }).subscribe({
      next: () => this.getDocument(this.document.id).subscribe()
    });
  }

  openUpdateSignerModal(signer: Signer) {
    this.updateSignerModalService
      .open({ signer })
      .pipe(tap(() => this.getDocument(this.document.id).subscribe()))
      .subscribe({
        next: data => {
          if (data) {
            signer.email = data.email;
            signer.public_id = data.public_id || signer.public_id;
            signer.parent = data.parent || signer.parent;
            signer.user.name = data.user.name;
            signer.user.cpf = data.user.cpf;
            signer.user.birthday = data.user.birthday;
            signer.configs.overwrite_name = null;
            signer.configs.overwrite_date = null;
          } else {
            this.notyService.error(this.translateService.instant('notyService.duplicateSignersWithSameAction'));
          }
        },
        complete: () => {
          this.loadDocument(this.document.id).subscribe({
            next: () => this.setCurrentSignerFields()
          });
        }
      });
  }

  openSignLinkModal(signer: Signer) {
    this.signLinkModalService.open({ signer }).subscribe();
  }

  openConsultCPFModal(signer: Signer) {
    this.consultCPFModalService
      .open({ signer })
      .pipe(tap(() => this.getDocument(this.document.id).subscribe()))
      .subscribe();
  }

  openVerificationDataModal(signer: Signer) {
    this.verificationDataModalService
      .open({ signer, hideManualApproval: !this.isAdmin() }, signer.verifications.find(verification => verification.type === SecurityVerificationEnum.Live) ? { size: 'lg' } : {})
      .pipe(filter(data => !!data))
      .subscribe({
        next: ({ choice, verification }) => {
          this.loaderService.show();
          let subscribe$: Observable<any>;

          switch (choice) {
            case ManualBiometryApprovalEnum.Accepted:
              subscribe$ = this.documentService.approveBiometric({ publicId: signer.public_id, verificationId: verification.id });
              break;

            case ManualBiometryApprovalEnum.Rejected:
              subscribe$ = this.documentService.rejectBiometric({ publicId: signer.public_id, verificationId: verification.id });
              break;

            case ManualBiometryApprovalEnum.ResetBiometryAttempt:
              subscribe$ = this.verificationService.resetBiometricAttempts({ security_verification_id: verification.id });
              break;
          }

          subscribe$.pipe(finalize(() => this.loaderService.hide())).subscribe({
            next: () => {
              this.getDocument(this.document.id).subscribe();
              if (this.isHistoryAlreadyOpened) {
                this.historyComponent.refreshHistory();
              }
            },
            error: error => this.errorHandlerService.handle(error)
          });
        }
      });
  }

  clearPdfCache() {
    this.documentService.clearPdfCache({ id: this.document.id }).subscribe({
      error: error => this.errorHandlerService.handle(error)
    });
  }

  resendDocumentWebhook() {
    this.documentService.resendWebhook({ documentId: this.document.id }).subscribe({
      next: () => this.notyService.success(this.translateService.instant('notyService.documentResendWebhook')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  isOwner(user?: User | Signer['user']) {
    user = user || this.currentUser;
    if (this.document && user) {
      return user.id === this.document.author.id;
    }
  }

  isAdmin() {
    return this.currentUser && this.document && this.documentService.isAdmin(this.currentUser, this.document);
  }

  hasMixedSignature() {
    return this.listingService.isMixedDocument(this.document);
  }

  isAutnq(user?: User) {
    user = user || this.currentUser;
    return !!(user && (this.currentUser.email || '').match(/@autentique.com.br$/));
  }

  isCurrentSigner(signer: Signer) {
    return (this.currentSigners || []).map(sig => sig.public_id).includes(signer.public_id);
  }

  isSignaturePage() {
    return !!location.href.match('/assinar/.+');
  }

  isPhoneDeliveryMethod(signer: CustomSigner) {
    return !!([DeliveryMethodEnum.DeliveryMethodSms, DeliveryMethodEnum.DeliveryMethodWhatsapp].includes(signer.delivery_method) && signer.user?.phone) || this.isForcePhoneDeliveryMethod(signer);
  }

  isForcePhoneDeliveryMethod(signer: CustomSigner) {
    return signer.user?.phone && !signer.user?.email && signer.delivery_method === DeliveryMethodEnum.DeliveryMethodLink;
  }

  canBeSigned(document?: Document, signer?: Signer) {
    document = document || this.document;
    signer = signer || this.currentSigners[0];
    return (
      !!signer?.action &&
      !document.is_blocked &&
      !this.documentService.isPastDeadline(document) &&
      !this.documentService.isSignerUnapproved(signer) &&
      this.documentService.isSignerPending(signer, { ignoreEmailErrors: true })
    );
  }

  scrollingRequired() {
    if (this.document.scrolling_required && !this.pdfViewer.renderOnIframe) {
      return this.doABarrelScroll;
    } else return false;
  }

  logout() {
    this.authenticationService.logout().subscribe({
      next: () => this.router.navigate(['/entrar'], { replaceUrl: true })
    });
  }

  changeSidebarContainer(container: 'signers' | 'history') {
    if (container === 'signers') {
      this.sidebarContainer = 'signers';
    }
    if (container === 'history' && this.document) {
      this.signersFieldsData = this.appService.getFieldsDataToShowOnHistory(this.document, this.templateVariables);

      if (this.historyComponent.documentData.id == null && this.isShowingHistoryMobile === false) {
        this.historyComponent.documentData = {
          id: this.document.id,
          created_at: this.document.created_at,
          author: {
            name: this.document.author.name,
            email: this.document.author.email
          }
        };
        this.historyComponent.refreshHistory();
      }
      if (this.historyComponentMobile.documentData.id == null && this.isShowingHistoryMobile === true) {
        this.historyComponentMobile.documentData = {
          id: this.document.id,
          created_at: this.document.created_at,
          author: {
            name: this.document.author.name,
            email: this.document.author.email
          }
        };
        this.historyComponentMobile.refreshHistory();
      }

      this.sidebarContainer = 'history';
      this.isHistoryAlreadyOpened = true;
    }
  }

  toggleBlock() {
    const isBlocked = this.documentService.isPastDeadline(this.document);
    this.alertModalService
      .confirmation({
        text:
          this.translateService.instant(isBlocked ? 'alerts.unblockDocument' : 'alerts.blockDocument') +
          this.translateService.instant(isBlocked ? 'alerts.allowPendingSigners' : 'alerts.preventPendingSigners'),
        confirmButtonText: this.translateService.instant(isBlocked ? 'button.unblock' : 'button.block')
      })
      .pipe(switchMap(() => this.documentService[isBlocked ? 'unblock' : 'block']([this.document.id])))
      .subscribe({
        next: () => this.notyService.success(this.translateService.instant('notyService.' + (isBlocked ? 'documentUnblockedSuccess' : 'documentBlockedSuccess'))),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  backButtonLabel() {
    const url = this.appService.previousUrl;
    if (url === '/documentos/grupo') {
      return this.translateService.instant('menu.wholeGroup');
    } else if (url === '/documentos/organizacao') {
      return this.translateService.instant('menu.wholeOrganization');
    } else if ((url || '').includes('/documentos/grupo/')) {
      return this.translateService.instant('menu.documentsGroup');
    } else if ((url || '').includes('/documentos/organizacao/')) {
      return this.translateService.instant('menu.documentsUser');
    } else if ((url || '').includes('/pastas/')) {
      return this.translateService.instant('menu.documentsFolder');
    } else {
      return this.translateService.instant('menu.myDocuments');
    }
  }

  updateBackLink(): void {
    const url = this.appService.previousUrl;
    if (
      url === '/documentos/grupo' ||
      url === '/documentos/organizacao' ||
      (url || '').includes('/documentos/grupo/') ||
      (url || '').includes('/documentos/organizacao/') ||
      (url || '').includes('/pastas/')
    ) {
      this.backLink = url;
    } else {
      this.backLink = '/documentos/todos';
    }
  }

  needsElectronLogin() {
    return environment.electron && !this.credentialsService.isAuthenticated() && !this.isSignaturePage();
  }

  redirectToLogin() {
    this.router.navigate(['/entrar'], { queryParams: { redirect: location.pathname } });
  }

  sendSignerEventWithPostMessage(eventMessage: 'signed' | 'rejected' = 'signed') {
    return tap(() => {
      if (!['signed', 'rejected'].includes(eventMessage)) return;
      window.parent.postMessage(eventMessage, '*');
    });
  }

  checkScrollingRequiredMobile() {
    if (this.document.scrolling_required) {
      this.doABarrelScroll = true;
    }
  }

  isSigningPurpose(): boolean {
    return this.credentialsService.credentials?.purpose === Purpose.Signing;
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    const scrollTop = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (scrollTop >= maxScroll - 40) {
      this.doABarrelScroll = false;
    }
  }
  @HostListener('window:touchmove')
  onWindowTouch() {
    const scrollTop = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (scrollTop >= maxScroll - 150) {
      this.doABarrelScroll = false;
    }
  }
  @HostListener('window:resize')
  onWindowReize() {
    this.isMobile = window.innerWidth <= 992;
    this.updateRenderedDocumentScale();
  }

  private signDocument(padesSignatureData: PadesSignatureData) {
    const signers = this.currentSigners;
    return this.signDocumentRequest(signers, padesSignatureData).pipe(
      tap(() => this.documentSignedModalService.open({ currentSigner: signers[0], document: this.document, filledFields: this.fieldsToFill?.length > 0 }).subscribe())
    );
  }

  private signDocumentRequest(signers: CustomSigner[], padesSignatureData: PadesSignatureData) {
    this.isLoading = true;
    this.loaderService.show();

    const prevStep$ = this.document.is_dynamic_document && this.fieldsToFill.length > 0 ? this.fillFields() : of(null); // Para preencher os campos ao assinar
    return prevStep$.pipe(
      switchMap(() => {
        let ids: string[];
        const isMixed = this.listingService.isMixedDocument(this.document);
        ids = isMixed ? [signers[0]?.signature_id].filter(id => !!id) : signers.map(signer => signer.signature_id).filter(id => !!id);
        ids = ids.length === 0 ? [this.route.snapshot.params.id] : ids;
        const isQualifiedSignature = this.document.qualified || signers[0]?.type === SignerTypeEnum.Qualified;
        if (isQualifiedSignature) {
          return [SignatureTypesEnum.Safeid, SignatureTypesEnum.Vidaas, SignatureTypesEnum.Birdid, SignatureTypesEnum.Dscloud].includes(padesSignatureData.type)
            ? this.documentService.signCloudCert({ id: ids[0], type: padesSignatureData.type })
            : this.documentService.signPades({ id: ids[0], type: padesSignatureData.type, payload: padesSignatureData.padesSignature });
        } else {
          return this.documentService.sign(ids);
        }
      }),
      this.sendSignerEventWithPostMessage('signed'),
      finalize(() => {
        this.isLoading = false;
        this.loaderService.hide();
        this.googleAnalyticsService.emitGTMEvent(GTMEventType.DocumentSigned, this.currentUser.id);
      }),
      this.afterSignPipe(),
      catchError(error => {
        let validations = error?.graphQLErrors?.[0]?.extensions?.validation;
        validations = (Array.isArray(validations) ? validations : [validations]).filter(v => !!v && !/^incorrect_cert_/.test(v));

        if (error?.message === 'signer_validation' && validations.length > 0) {
          const errorsObject = Object.fromEntries(
            validations.map((fieldError: string) => {
              // @ts-ignore
              let value = this.currentUser[fieldError.replace('incorrect_', '')];
              if (fieldError === 'incorrect_birthdate') {
                value = this.currentUser.birthday; // sempre volta no formato DD/MM/YYYY no back
                const [day, month, year] = value.split('/');
                if (day && month && year) value = `${year}-${month}-${day}`; // DD/MM/YYYY -> YYYY-MM-DD
              }
              return [fieldError, value];
            })
          );
          window.parent.postMessage({ type: 'incorrect_fields', errors: errorsObject }, '*');
          this.notyService.error(this.translateService.instant('notyService.incorrectSignerValidation'));
          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  private rejectDocumentRequest(signers: CustomSigner[], reason?: string) {
    this.isLoading = true;

    const prevStep$ = this.document.is_dynamic_document && this.fieldsToFill.length > 0 ? this.fillFields(true) : of(null); // Para preencher os campos ao recusar
    return prevStep$.pipe(
      switchMap(() =>
        this.documentService
          .reject(
            this.documentService.isSignatureByLink(this.route.snapshot.params.id) ? [this.route.snapshot.params.id] : signers.map(signer => signer.signature_id).filter(id => !!id),
            this.documentService.isSignatureByLink(this.route.snapshot.params.id) ? SignatureTypeEnum.Link : SignatureTypeEnum.Signature,
            reason || null
          )
          .pipe(
            this.sendSignerEventWithPostMessage('rejected'),
            finalize(() => {
              this.isLoading = false;
            }),
            this.afterSignPipe()
          )
      )
    );
  }

  private openPreSignModal() {
    return of(null).pipe(
      switchMap(() => (this.currentUser ? of(this.currentUser) : this.userService.getCurrentUser({ fetchPolicy: 'cache-first' }))),
      tap(user => !user?.currentPermissions?.sign_documents && this.notyService.error(this.translateService.instant('error_without_permission_sign_documents'))),
      takeWhile(user => user?.currentPermissions?.sign_documents),
      this.handleVerifications([SecurityVerificationEnum.Sms]),
      this.handleVerifications([SecurityVerificationEnum.BiometricAndTextExtraction, SecurityVerificationEnum.LivenessAndTextExtraction]),
      switchMap(() => this.setUserInfoPipe()),
      this.handleVerifications(this.documentService.allowedManualVerifications.filter(type => type !== SecurityVerificationEnum.BiometricAndTextExtraction)),
      switchMap((data: SecurityVerificationType[]) => {
        const hasNonBasicVerification = this.currentSigners[0]?.verifications?.some(v => v.type !== SecurityVerificationEnum.Sms && v.type !== SecurityVerificationEnum.Manual);
        return hasNonBasicVerification ? this.loadDocument(this.route.snapshot.params.id).pipe(map(() => data)) : of(data);
      }),
      switchMap((data: SecurityVerificationType[]) =>
        this.preSignModalService.open({
          action: this.currentSigners[0].action.name,
          signer: this.currentSigners[0] as Signer,
          padesSignatureId: this.currentSigners[0].signature_id || this.route.snapshot.params.id,
          document: this.document,
          isSignerQualified: this.currentSigners[0]?.type === SignerTypeEnum.Qualified,
          hasFailedBiometry: !!(data || []).find(
            verification => (!verification.verified_at && this.documentService.automatedVerifications.includes(verification.type)) || (verification.max_attempt === 0 && !verification.verified_at)
          )
        })
      )
    );
  }

  private handleVerifications(types?: SecurityVerificationEnum[]) {
    return pipe(
      map(() =>
        flatten(this.currentSigners.map(signer => signer.verifications?.filter(value => !!value && (!types || types.includes(value.type))))).map(verification =>
          (verification.type === SecurityVerificationEnum.Sms
            ? this.verificationSmsModalService
            : [SecurityVerificationEnum.Upload, SecurityVerificationEnum.Live].includes(verification.type)
              ? this.verificationIdAnalyzerModalService
              : this.verificationModalService
          )
            .open({ verification, documentId: this.document.id, dismissAsError: true })
            .pipe(map(data => merge(verification, data)))
        )
      ),
      switchMap(verificationsBiometryModals => concat(...verificationsBiometryModals).pipe(toArray())),
      switchMap(data => {
        const isUnverified = data && data.find(v => !v.verified_at && !this.documentService.allowedManualVerifications.includes(v.type));
        const isUnverifiedWithFallbackDisabled =
          data && data.find(v => !v.verified_at && this.documentService.automatedVerifications.includes(v.type) && v.fallback_behavior === FallbackBehaviorEnum.DisableFallback);

        if (isUnverifiedWithFallbackDisabled) {
          return this.alertModalService
            .warning({
              title: this.translateService.instant('alerts.biometryFailedTitle'),
              text: this.translateService.instant('alerts.biometryFailedNoFallback'),
              confirmButtonText: false,
              dismissAsError: true
            })
            .pipe(
              catchError(() => of(null)),
              this.afterSignPipe(),
              switchMap(() => of())
            );
        } else if (isUnverified) {
          return of(null).pipe(
            this.afterSignPipe(),
            switchMap(() => of())
          );
        }

        return of(data);
      }),
      catchError(err => (err ? throwError(() => err) : of()))
    );
  }

  private setUserInfoPipe(signers?: CustomSigner[]) {
    const currentSigners = signers || this.currentSigners;
    const serproVerification = currentSigners.find(signer =>
      (signer.verifications || []).find(verification => [SecurityVerificationEnum.PfFacial, SecurityVerificationEnum.PfFacialMatch].includes(verification.type))
    ); // Serpro precisa CPF
    const prefilledFields = this.getPrefilledFieldsWithBiometric(currentSigners);
    const isNewUserRegistrationFlow = !this.currentUser?.name;

    return this.setMissingUserInfoModalService
      .openIfMissingUserInfo({
        ignoreEmailAndPassword: this.isPhoneDeliveryMethod(currentSigners[0]) || !!this.currentUser?.phone,
        ignoreCpf: this.document.ignore_cpf && !serproVerification,
        forceBirthdateAs: currentSigners[0] && currentSigners[0].configs?.birthdate,
        forceCpfAs: !this.document.qualified && currentSigners[0] && currentSigners[0].configs.cpf,
        forceNameAs: currentSigners[0] && currentSigners[0].configs.name,
        ignoreBirthdate: this.document.ignore_birthdate,
        prefilledFields,
        isSigningFlow: true,
        documentLocale: this.document.locale
      })
      .pipe(
        tap(data => {
          if (!!data && isNewUserRegistrationFlow) {
            this.googleAnalyticsService.emitGTMEvent(GTMEventType.SignUpCompleted, this.currentUser.id, { signup_origin: 'invite' });
          }
        }),
        switchMap(() => this.chooseSignatureModalService.open({ user: this.currentUser, document: this.document, signers: currentSigners }))
      );
  }

  private getPrefilledFieldsWithBiometric(currentSigners: CustomSigner[]): PrefilledFields {
    const biometricVerification = flatten(
      currentSigners.map(s => s.verifications?.filter(v => [SecurityVerificationEnum.BiometricAndTextExtraction, SecurityVerificationEnum.LivenessAndTextExtraction].includes(v?.type)) || [])
    )[0];
    const extracted = biometricVerification?.user?.extracted_data;
    const prefilled = currentSigners[0]?.configs?.prefilled_fields;
    return {
      ...prefilled,
      name: prefilled?.name || (extracted?.first_name && extracted?.last_name ? `${extracted.first_name} ${extracted.last_name}` : undefined),
      cpf: prefilled?.cpf || extracted?.cpf,
      birthdate: prefilled?.birthdate || extracted?.date_of_birth
    };
  }

  private afterSignPipe() {
    return tap({
      next: () => {
        this.currentSigners = [];
        this.getDocument(this.document.id).subscribe();
        if (this.isHistoryAlreadyOpened) {
          this.historyComponent.refreshHistory();
        }
      }
    });
  }

  private setCurrentSignersPipe() {
    return tap<SignatureToken[]>(
      tokens => (this.currentSigners = tokens.map(token => ({ signature_id: token.signature_id, ...this.document.signatures.find(sig => sig.public_id === token.signature_public_id) })))
    );
  }
}
